import EventJumpStory from '@/model/EventClass/EventJumpStory'
import EventMovePic from '@/model/EventClass/EventMovePic'
import EventPicShow from '@/model/EventClass/EventPicShow'
import EventStartBGM from '@/model/EventClass/EventStartBGM'
import EventText from '@/model/EventClass/EventText'
import EventTextChoice from '@/model/EventClass/EventTextChoice'
import EventTextDif from '@/model/EventClass/EventTextDif'
import EventTextEnd from '@/model/EventClass/EventTextEnd'
import EventWait from '@/model/EventClass/EventWait'
import MapInfo from '@/model/MapInfo'
import type StoryTree from '@/model/StoryTree'
import ModuleId from '@/model/DataStructModuleId'
import { NameOfColor } from '@/model/UserUseRember'
import { nextAvailableMapId, reorderMapsDepthFirst } from '@/control/storyMapOperations'
import { formatStoryMapId } from '@/features/story/storyTreeProjection'
import { stripEmoji } from '@/utils/plainTextInput'
import { normalizeRgbString, normalizeTextValue } from '@/utils/textColor'

export type TextImportMode = 'basic' | 'advanced'

export interface BasicTextImportRow {
  id: string
  mapName: string
  mapId?: number
  backPic: string
  backMusic: string
  name: string
  content: string
}

export interface AdvancedTextImportRow {
  uid: string
  mapName: string
  /** 当前行是否位于选项块内（不含选项开始/结束/选项项结构行）。 */
  inOptionBlock?: boolean
  optionKey: {
    type: 'start' | 'end' | 'item' | ''
    text: string
  }
  optionContent: string
  bgm: string
  backPic: string
  textContent: string
  person: {
    name: string
    half: {
      halfPath: string
      halfId: number
      x: number
      y: number
    }
  }
}

const ADVANCED_OPTION_KEY_TYPES = new Set(['start', 'end', 'item'])

/**
 * 规范化高级导入行（云盘恢复时补齐 optionKey，避免类型列空白）。
 */
export function normalizeAdvancedTextImportRow(row: AdvancedTextImportRow): AdvancedTextImportRow {
  const optionKey = row.optionKey && typeof row.optionKey === 'object'
    ? row.optionKey
    : { type: '' as const, text: '' }
  const rawType = String(optionKey.type ?? '')
  const type = ADVANCED_OPTION_KEY_TYPES.has(rawType)
    ? (rawType as 'start' | 'end' | 'item')
    : ''
  row.optionKey = { type, text: String(optionKey.text ?? '') }
  if (typeof row.inOptionBlock !== 'boolean') {
    row.inOptionBlock = false
  }
  return row
}

export function normalizeAdvancedTextImportRows(rows: AdvancedTextImportRow[]): AdvancedTextImportRow[] {
  return rows.map((row) => normalizeAdvancedTextImportRow(row))
}

export interface AdvancedSymbolSettings {
  namePrepend: string
  nameAppend: string
  nameWrap: boolean
  optionRecognize: boolean
  optionStart: string
  optionItem: string
  optionEnd: string
  sceneRecognize: boolean
  scenePrepend: string
  sceneAppend: string
  sceneWrap: boolean
  rows: number
  columns: number
}

export interface TextImportProjectContext {
  storyTree: StoryTree
  currentMap: MapInfo
  fontColor: string
  /** 工程角色缓存 `UserUseRember.NameList`，导入后同步出现过的角色名。 */
  roleNameList: NameOfColor[]
}

export interface TextImportApplyResult {
  insertedEvents: number
  createdMaps: number
}

function createUid(): string {
  return Math.random().toString(16).slice(2)
}

function normalizeLineCount(value: number): number {
  return Math.max(1, Math.min(100, Math.round(Number(value) || 1)))
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getBaseName(path: string): string {
  const normalized = path.replace(/\\/g, '/')
  const name = normalized.split('/').pop() ?? ''
  return name.replace(/\.\w+$/, '')
}

function appendWrappedText(value: string, columns: number): string {
  const size = normalizeLineCount(columns)
  if (!value) return ''
  return value.replace(new RegExp(`(.{${size}})`, 'g'), '$1\n').replace(/\n$/, '')
}

/**
 * 将正文中「前面不是反斜杠」的换行写成字面量 `\n`，供 EventText.Parameter 存储。
 * 不使用 RegExp 后行断言，避免旧版 iPad Safari 解析失败导致白屏。
 */
function escapeContentNewlines(content: string): string {
  let out = ''
  for (let i = 0; i < content.length; i++) {
    const ch = content[i]!
    if (ch === '\n' && (i === 0 || content[i - 1] !== '\\')) {
      out += '\\n'
      continue
    }
    out += ch
  }
  return out
}

function createTextEvent(name: string, content: string, fontColor: string, indent = 0): EventText {
  const event = new EventText()
  event.RoleName = name
  event.RoleColor = fontColor
  event.Content = escapeContentNewlines(content)
  event.Indent = indent
  return event
}

function collectUniqueRoleNames(names: string[]): string[] {
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const raw of names) {
    const name = normalizeTextValue(raw)
    if (!name || seen.has(name)) continue
    seen.add(name)
    ordered.push(name)
  }
  return ordered
}

/**
 * 将本次「完成」写入的显示文本事件中的角色名同步到 `NameList` 头部。
 * 仅在用户点击完成、事件已落库后调用；已存在则补全空颜色并提升到列表头部。
 * @param nameList 工程 `userRm.NameList`
 * @param names 与本次写入的 `EventText` 一一对应、按出现顺序去重后的角色名
 * @param fontColor 总设对话默认色，写入新角色
 */
export function registerImportedRoleNames(
  nameList: NameOfColor[],
  names: string[],
  fontColor: string,
): void {
  const color = normalizeRgbString(fontColor) || normalizeTextValue(fontColor)
  const ordered = collectUniqueRoleNames(names)
  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const name = ordered[index]!
    const foundIndex = nameList.findIndex((item) => normalizeTextValue(item?.Name) === name)
    if (foundIndex >= 0) {
      const found = nameList[foundIndex]!
      if (!normalizeRgbString(found.Color) && color) {
        found.Color = color
      }
      nameList.splice(foundIndex, 1)
      nameList.unshift(found)
      continue
    }
    const next = new NameOfColor()
    next.Name = name
    next.Color = color
    nameList.unshift(next)
  }
}

function createBgmEvent(path: string, indent = 0): EventStartBGM {
  const event = new EventStartBGM()
  event.Path = path
  event.Description = getBaseName(path)
  event.Indent = indent
  return event
}

function createPicShowEvent(path: string, picId: number, indent = 0): EventPicShow {
  const event = new EventPicShow()
  event.PicPath = path
  event.PicId = picId
  event.Indent = indent
  return event
}

function createJumpStoryEvent(map: MapInfo, indent = 0): EventJumpStory {
  const event = new EventJumpStory()
  event.MapId = map.Id
  event.Description = `${formatStoryMapId(map.Id)}: ${map.Name}，画面淡出0帧`
  event.FadeFrame = 0
  event.Indent = indent
  return event
}

function createImportedMap(storyTree: StoryTree, baseMap: MapInfo, name: string): MapInfo {
  const allIds = new Set(storyTree.Maps.map((map) => map.Id))
  const parentId = allIds.has(baseMap.ParentId) ? baseMap.ParentId : 0
  const sameParentMaps = storyTree.Maps.filter((map) => {
    const mapParentId = allIds.has(map.ParentId) ? map.ParentId : 0
    return mapParentId === parentId
  })
  const nextMap = new MapInfo({
    name,
    id: nextAvailableMapId(storyTree.Maps),
    parentId,
    order: sameParentMaps.length,
    list: [],
  })
  nextMap.mid = ModuleId.mapInfo + nextMap.Id
  nextMap.IsHard = baseMap.IsHard
  storyTree.Maps.push(nextMap)
  storyTree.Events[String(nextMap.Id)] = nextMap.List
  reorderMapsDepthFirst(storyTree)
  storyTree.createStoryTree()
  return nextMap
}

type BasicImportParsedLine = {
  text: string
  /** 是否为按列数自动折行产生的续行（应并入上一条显示文本，而不是新事件）。 */
  isColumnWrap: boolean
}

/**
 * 将初级导入原文拆成行，并标记列宽自动折行续行。
 * @param text 用户粘贴文本。
 * @param columns 每行字数。
 * @param nameReg 姓名行正则。
 */
function splitBasicImportLines(text: string, columns: number, nameReg: RegExp): BasicImportParsedLine[] {
  const parsed: BasicImportParsedLine[] = []
  const sourceLines = text.split('\n').map((line) => line.trim()).filter(Boolean)
  for (const sourceLine of sourceLines) {
    if (nameReg.test(sourceLine)) {
      parsed.push({ text: sourceLine, isColumnWrap: false })
      continue
    }
    if (sourceLine.length <= columns) {
      parsed.push({ text: sourceLine, isColumnWrap: false })
      continue
    }
    let rest = sourceLine
    let isContinuation = false
    while (rest.length > columns) {
      parsed.push({ text: rest.slice(0, columns), isColumnWrap: isContinuation })
      rest = rest.slice(columns)
      isContinuation = true
    }
    if (rest) {
      parsed.push({ text: rest, isColumnWrap: isContinuation })
    }
  }
  return parsed
}

/** 统计显示文本在对话框中占用的行数（按 `\n` 分段）。 */
function countContentDisplayLines(content: string): number {
  if (!content.trim()) return 0
  return content.split('\n').length
}

/**
 * 按旧版初级规则解析粘贴文本，生成结果表行。
 * @param text 用户输入的剧本文本。
 * @param options 当前剧情、行列和姓名识别配置。
 * @returns 初级导入结果行。
 */
export function parseBasicTextImport(
  text: string,
  options: {
    rows: number
    columns: number
    nameIdentify: boolean
    mapId: number
    mapName: string
  },
): BasicTextImportRow[] {
  const maxRowsPerEvent = normalizeLineCount(options.rows)
  const columns = normalizeLineCount(options.columns)
  const nameReg = new RegExp(`^【.{1,${columns}}】$`)
  const lines = splitBasicImportLines(stripEmoji(text), columns, nameReg)

  const rows: BasicTextImportRow[] = []
  for (let index = 0; index < lines.length; index += 1) {
    const { text: line, isColumnWrap } = lines[index]!
    const row: BasicTextImportRow = {
      id: createUid(),
      mapName: index === 0 ? options.mapName : '',
      mapId: index === 0 ? options.mapId : 0,
      backPic: '',
      backMusic: '',
      name: '',
      content: '',
    }
    if (options.nameIdentify && nameReg.test(line)) {
      row.name = line.slice(1, -1)
      rows.push(row)
      continue
    }
    const prev = rows[rows.length - 1]
    if (prev && !prev.content) {
      prev.content = line
    } else if (prev && prev.content && isColumnWrap) {
      if (countContentDisplayLines(prev.content) < maxRowsPerEvent) {
        prev.content = `${prev.content}\n${line}`
      } else {
        row.name = prev.name
        row.content = line
        rows.push(row)
      }
    } else {
      row.content = line
      rows.push(row)
    }
  }
  return rows
}

/**
 * 按高级文本导入识别符解析剧本文本，生成四步向导的结果表数据。
 * @param text 用户输入的剧本文本。
 * @param settings 姓名、选项、剧情和行列配置。
 * @returns 高级导入结果行与错误信息。
 */
export function parseAdvancedTextImport(
  text: string,
  settings: AdvancedSymbolSettings,
): { rows: AdvancedTextImportRow[]; error?: string } {
  const columns = normalizeLineCount(settings.columns)
  const rows = normalizeLineCount(settings.rows)
  const nameReg = new RegExp(
    `^${escapeRegExp(settings.namePrepend)}.+${escapeRegExp(settings.nameAppend)}${settings.nameWrap ? '$' : ''}`,
  )
  const mapNameReg = new RegExp(
    `^${escapeRegExp(settings.scenePrepend)}.+${escapeRegExp(settings.sceneAppend)}`,
  )
  const optionStartReg = new RegExp(`^${escapeRegExp(settings.optionStart)}$`)
  const optionEndReg = new RegExp(`^${escapeRegExp(settings.optionEnd)}$`)
  const optionItemReg = new RegExp(`^${escapeRegExp(settings.optionItem)}`)
  const list: AdvancedTextImportRow[] = []
  const lines = stripEmoji(text).split('\n')
  let nest = 0

  for (let index = 0; index < lines.length; index += 1) {
    const line = (lines[index] ?? '').trim()
    if (!line) continue
    const item: AdvancedTextImportRow = {
      uid: createUid(),
      mapName: '',
      optionKey: { type: '', text: '' },
      optionContent: '',
      bgm: '',
      backPic: '',
      textContent: '',
      person: {
        name: '',
        half: {
          halfPath: '',
          halfId: 5,
          x: 0,
          y: 0,
        },
      },
    }

    if (nameReg.test(line)) {
      const matched = line.match(nameReg)?.[0] ?? ''
      item.person.name = matched.slice(settings.namePrepend.length, matched.length - settings.nameAppend.length)
      if (settings.nameWrap) {
        const nextLine = (lines[index + 1] ?? '').trim()
        const blocked = nameReg.test(nextLine)
          || (settings.sceneRecognize && mapNameReg.test(nextLine))
          || (settings.optionRecognize && optionStartReg.test(nextLine))
          || (settings.optionRecognize && optionEndReg.test(nextLine))
          || (nest !== 0 && settings.optionRecognize && optionItemReg.test(nextLine))
        if (nextLine && !blocked) {
          item.textContent = appendWrappedText(nextLine, columns)
          index += 1
        }
      } else {
        item.textContent = appendWrappedText(line.replace(nameReg, ''), columns)
      }
    } else if (settings.sceneRecognize && mapNameReg.test(line)) {
      const matched = line.match(mapNameReg)?.[0] ?? ''
      item.mapName = matched.slice(settings.scenePrepend.length, matched.length - settings.sceneAppend.length)
      if (!settings.sceneWrap) {
        item.textContent = appendWrappedText(line.replace(mapNameReg, ''), columns)
      } else {
        const nextLine = (lines[index + 1] ?? '').trim()
        if (nextLine && !optionStartReg.test(nextLine) && !nameReg.test(nextLine)) {
          item.textContent = appendWrappedText(nextLine, columns)
          index += 1
        }
      }
    } else if (settings.optionRecognize && optionStartReg.test(line)) {
      item.optionKey = { type: 'start', text: line }
      nest += 1
    } else if (settings.optionRecognize && optionEndReg.test(line)) {
      item.optionKey = { type: 'end', text: line }
      nest -= 1
    } else if (nest > 0 && settings.optionRecognize && optionItemReg.test(line)) {
      item.optionKey = { type: 'item', text: line.match(optionItemReg)?.[0] ?? '' }
      item.optionContent = line.replace(optionItemReg, '')
    } else {
      item.textContent = appendWrappedText(line, columns)
    }

    if (item.person.name) {
      const existingPerson = list.find((row) => row.person.name === item.person.name)
      if (existingPerson) {
        item.person = JSON.parse(JSON.stringify(existingPerson.person))
      }
    }
    item.inOptionBlock = nest > 0 && !item.optionKey.type
    const chunks = item.textContent.split('\n')
    while (chunks.length > rows) {
      const copy = JSON.parse(JSON.stringify(item)) as AdvancedTextImportRow
      copy.uid = createUid()
      copy.textContent = chunks.splice(0, rows).join('\n')
      if (copy.textContent || copy.person.name) list.push(copy)
    }
    item.textContent = chunks.join('\n')
    if (item.textContent || item.person.name || item.optionKey.text || item.mapName) {
      list.push(item)
    }
  }

  if (nest !== 0) return { rows: [], error: '选项开始数与选项结束数不匹配' }
  return { rows: normalizeAdvancedTextImportRows(list) }
}

/**
 * 将初级文本导入结果写入剧情树。
 * @param context 当前工程和剧情上下文。
 * @param rows 初级导入结果行。
 * @returns 写入统计。
 */
export function applyBasicTextImport(
  context: TextImportProjectContext,
  rows: BasicTextImportRow[],
): TextImportApplyResult {
  const { storyTree, currentMap, fontColor } = context
  const mapByName = new Map<string, MapInfo>()
  let activeMap = currentMap
  let insertedEvents = 0
  let createdMaps = 0
  const importedRoleNames: string[] = []

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index]!
    if (row.mapId === currentMap.Id && row.mapName === currentMap.Name) {
      activeMap = currentMap
    } else if (row.mapName.trim()) {
      activeMap = mapByName.get(row.mapName) ?? createImportedMap(storyTree, currentMap, row.mapName.trim())
      if (!mapByName.has(row.mapName)) {
        mapByName.set(row.mapName, activeMap)
        createdMaps += 1
      }
    }
    const list = storyTree.loadEvent(activeMap.Id)
    if (row.backMusic) list.push(createBgmEvent(row.backMusic))
    if (row.backPic && row.backPic !== rows[index - 1]?.backPic) {
      const show = createPicShowEvent(row.backPic, 2)
      show.Opacity = 0
      list.push(show)
      const move = new EventMovePic()
      move.PicId = 2
      move.PicPath = row.backPic
      move.Opacity = 255
      move.Time = 60
      list.push(move)
      const wait = new EventWait()
      wait.Time = 30
      list.push(wait)
    }
    list.push(createTextEvent(row.name, row.content, fontColor))
    const roleName = normalizeTextValue(row.name)
    if (roleName) importedRoleNames.push(roleName)
    insertedEvents += 1
  }
  registerImportedRoleNames(context.roleNameList, importedRoleNames, fontColor)
  storyTree.createStoryTree()
  return { insertedEvents, createdMaps }
}

/**
 * 将高级文本导入结果写入剧情树。
 * @param context 当前工程和剧情上下文。
 * @param rows 高级导入结果行。
 * @returns 写入统计。
 */
export function applyAdvancedTextImport(
  context: TextImportProjectContext,
  rows: AdvancedTextImportRow[],
): TextImportApplyResult {
  const { storyTree, currentMap, fontColor } = context
  let activeMap = currentMap
  let activeList = storyTree.loadEvent(activeMap.Id)
  let indent = 0
  let insertedEvents = 0
  let createdMaps = 0
  const textDifStack: EventTextDif[] = []
  const importedRoleNames: string[] = []

  for (const row of rows) {
    if (row.mapName.trim()) {
      const nextMap = createImportedMap(storyTree, currentMap, row.mapName.trim())
      activeList.push(createJumpStoryEvent(nextMap, indent))
      activeMap = nextMap
      activeList = storyTree.loadEvent(activeMap.Id)
      createdMaps += 1
    }
    if (row.bgm && !row.optionKey.type) activeList.push(createBgmEvent(row.bgm, indent))
    if (row.backPic && !row.optionKey.type) activeList.push(createPicShowEvent(row.backPic, 2, indent))
    if ((row.textContent || row.person.name) && !row.optionKey.type) {
      if (row.person.half.halfPath) {
        const half = createPicShowEvent(row.person.half.halfPath, row.person.half.halfId, indent)
        half.X = row.person.half.x
        half.Y = row.person.half.y
        activeList.push(half)
      }
      activeList.push(createTextEvent(row.person.name, row.textContent, fontColor, indent))
      const roleName = normalizeTextValue(row.person.name)
      if (roleName) importedRoleNames.push(roleName)
      insertedEvents += 1
    }
    if (row.optionKey.type === 'start') {
      const event = new EventTextDif()
      event.Indent = indent
      textDifStack.unshift(event)
      activeList.push(event)
      indent += 1
    } else if (row.optionKey.type === 'item') {
      const previous = activeList[activeList.length - 1]
      if (!(previous instanceof EventTextDif)) indent = Math.max(0, indent - 1)
      const event = new EventTextChoice()
      event.Indent = indent
      event.Content = `：[${row.optionContent}] 的剧情`
      textDifStack[0]?.Parameter.push(row.optionContent)
      activeList.push(event)
      indent += 1
    } else if (row.optionKey.type === 'end') {
      indent = Math.max(0, indent - 2)
      const event = new EventTextEnd()
      event.Indent = indent
      textDifStack.shift()
      activeList.push(event)
    }
  }
  registerImportedRoleNames(context.roleNameList, importedRoleNames, fontColor)
  storyTree.createStoryTree()
  return { insertedEvents, createdMaps }
}
